import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Minus, Plus, Trash2, ArrowLeft, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { items, updateQuantity, removeItem, subtotal, itemCount } = useCart();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Cart - SizzleBox';
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const deliveryFee = settings?.delivery_fee || 500;
  const total = subtotal + deliveryFee;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="cart-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <Logo className="h-12" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-28 pb-20">
        <div className="container mx-auto px-6">
          {/* Back Link */}
          <Link to="/menu" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Continue Shopping
          </Link>

          <h1 className="font-heading text-5xl text-white mb-8">Your Cart</h1>

          {items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-20 h-20 text-gray-600 mx-auto mb-6" />
              <h2 className="text-2xl text-white mb-4">Your cart is empty</h2>
              <p className="text-gray-400 mb-8">Looks like you haven't added any items yet</p>
              <Link to="/menu">
                <Button className="bg-fire-500 hover:bg-fire-600 text-white rounded-full px-8 py-4">
                  Browse Menu
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map(item => (
                  <div
                    key={item.menu_item_id}
                    className="bg-card rounded-2xl p-4 border border-white/10 flex gap-4"
                    data-testid={`cart-item-${item.menu_item_id}`}
                  >
                    <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg">{item.name}</h3>
                      <p className="text-fire-500 font-bold">₦{item.price.toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeItem(item.menu_item_id)}
                        className="text-gray-500 hover:text-red-500 transition-colors"
                        data-testid={`remove-item-${item.menu_item_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      <div className="flex items-center gap-3 bg-black/50 rounded-full px-2 py-1">
                        <button
                          onClick={() => updateQuantity(item.menu_item_id, item.quantity - 1)}
                          className="text-white hover:text-fire-500 transition-colors p-1"
                          data-testid={`decrease-qty-${item.menu_item_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-white font-bold w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.menu_item_id, item.quantity + 1)}
                          className="text-white hover:text-fire-500 transition-colors p-1"
                          data-testid={`increase-qty-${item.menu_item_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-card rounded-2xl p-6 border border-white/10 sticky top-28">
                  <h2 className="text-white font-bold text-xl mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-400">
                      <span>Subtotal ({itemCount} items)</span>
                      <span className="text-white">₦{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Delivery Fee</span>
                      <span className="text-white">₦{deliveryFee.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex justify-between">
                        <span className="text-white font-bold text-lg">Total</span>
                        <span className="text-fire-500 font-bold text-xl">₦{total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <Link to="/checkout" data-testid="proceed-to-checkout">
                    <Button className="w-full bg-fire-500 hover:bg-fire-600 text-white rounded-full py-4 font-bold text-lg">
                      Proceed to Checkout
                    </Button>
                  </Link>

                  <a
                    href={`https://wa.me/${settings?.whatsapp?.replace(/[^0-9]/g, '')}?text=Hi! I'd like to place an order:%0A%0A${items.map(i => `${i.quantity}x ${i.name} - ₦${(i.price * i.quantity).toLocaleString()}`).join('%0A')}%0A%0ASubtotal: ₦${subtotal.toLocaleString()}%0ADelivery: ₦${deliveryFee.toLocaleString()}%0ATotal: ₦${total.toLocaleString()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-4"
                    data-testid="whatsapp-checkout"
                  >
                    <Button variant="outline" className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full py-4 font-bold">
                      Order via WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
